import { useEffect, useMemo, useState } from "react";
import { differenceInYears, parseISO } from "date-fns";

import { Button } from "components/ui/Button";
import { Checkbox } from "components/ui/Checkbox/Checkbox";
import { Dialog, DialogFooter } from "components/ui/Dialog";
import { Select } from "components/ui/Select/Select";
import { useEthnicGroups } from "features/patients/hooks/useEthnicGroups";
import { usePatientData } from "features/patients/hooks/usePatientData";
import { calculateQRisk3, smokingStatusToSmokeCat } from "utils/qrisk3";

import { mapEthnicityToQRisk } from "../utils/mapEthnicityToQRisk";
import styles from "./QRisk3CalculatorModal.module.css";

const normalizeDecimal = (val) => (val ? String(val).replace(",", ".") : "");
const parseNumeric = (val) => parseFloat(String(val).replace(",", "."));

const ETHNICITY_OPTIONS = [
  { label: "White or not stated", value: 1 },
  { label: "Indian", value: 2 },
  { label: "Pakistani", value: 3 },
  { label: "Bangladeshi", value: 4 },
  { label: "Other Asian background", value: 5 },
  { label: "Black Caribbean", value: 6 },
  { label: "Black African", value: 7 },
  { label: "Chinese", value: 8 },
  { label: "Other ethnic group", value: 9 },
];

const CONDITIONS = [
  { key: "b_AF", label: "Atrial fibrillation" },
  { key: "b_treatedhyp", label: "On blood pressure treatment" },
  { key: "b_type1", label: "Type 1 diabetes" },
  { key: "b_type2", label: "Type 2 diabetes" },
  {
    key: "fh_cvd",
    label: "Angina or heart attack in a 1st degree relative < 60 years",
  },
  { key: "b_ra", label: "Rheumatoid arthritis" },
  { key: "b_renal", label: "Chronic kidney disease (stage 3, 4 or 5)" },
  { key: "b_migraine", label: "Migraine" },
  { key: "b_sle", label: "Systemic lupus erythematosus (SLE)" },
  { key: "b_atypicalantipsy", label: "Atypical antipsychotics" },
  { key: "b_corticosteroids", label: "Regular corticosteroid tablets" },
  { key: "b_impotence2", label: "Erectile dysfunction (males only)" },
  {
    key: "b_semi",
    label: "Severe mental illness",
    hint: "Includes schizophrenia, bipolar disorder and moderate/severe depression",
  },
];

const getRiskLevel = (score) => {
  if (score < 10)
    return {
      level: "Low risk",
      description: "10-year CVD risk below 10%.",
      colorKey: "Low",
    };
  if (score < 20)
    return {
      level: "Moderate risk",
      description: "10-year CVD risk 10–20%. Consider lifestyle interventions.",
      colorKey: "Moderate",
    };
  if (score < 30)
    return {
      level: "High risk",
      description:
        "10-year CVD risk 20–30%. Eligible for CDM Prevention Programme.",
      colorKey: "High",
    };
  return {
    level: "Very high risk",
    description: "10-year CVD risk ≥30%. Urgent intervention recommended.",
    colorKey: "VeryHigh",
  };
};

const RESULT_CLASS = {
  Low: styles.resultLow,
  Moderate: styles.resultModerate,
  High: styles.resultHigh,
  VeryHigh: styles.resultVeryHigh,
};

const SCORE_CLASS = {
  Low: styles.scoreLow,
  Moderate: styles.scoreModerate,
  High: styles.scoreHigh,
  VeryHigh: styles.scoreVeryHigh,
};

export const QRisk3CalculatorModal = ({
  isOpen,
  onClose,
  onConfirm,
  onFieldChange,
  prefillBmi,
  prefillSbp,
  prefillSmokingStatus,
  prefillTotalCholesterol,
  prefillHdl,
  prefillSbps5,
  prefillEthrisk,
  prefillConditions,
  prefillCigarettesPerDay,
  selectedEthnicityCode,
}) => {
  const { data: patient } = usePatientData();
  const { data: ethnicGroups } = useEthnicGroups();

  // Seed the ethnicity from the ethnicity selected on THIS review (the
  // registration/initial step), falling back to the patient's HSE ethnic group
  // when the review has none. A stored review value (prefillEthrisk) always
  // wins; the ethnicity-derived value is only the default for a new review.
  const patientEthrisk = mapEthnicityToQRisk(
    selectedEthnicityCode ?? patient?.ethnicityCode,
    ethnicGroups
  );

  const patientAge = patient?.dateOfBirth
    ? differenceInYears(new Date(), parseISO(patient.dateOfBirth))
    : null;

  const patientGender = patient?.gender?.toUpperCase() ?? null;

  const [gender, setGender] = useState(patientGender ?? "");
  const isMale = gender === "MALE";

  const [ethrisk, setEthrisk] = useState(
    prefillEthrisk ?? patientEthrisk ?? 1
  );
  const [totalCholesterol, setTotalCholesterol] = useState(
    normalizeDecimal(prefillTotalCholesterol)
  );
  const [hdl, setHdl] = useState(normalizeDecimal(prefillHdl));
  const [sbp, setSbp] = useState(normalizeDecimal(prefillSbp));
  const [sbps5, setSbps5] = useState(normalizeDecimal(prefillSbps5));
  const [bmi, setBmi] = useState(normalizeDecimal(prefillBmi));
  const [cigarettesPerDay, setCigarettesPerDay] = useState(
    prefillCigarettesPerDay ? String(prefillCigarettesPerDay) : ""
  );
  const [conditions, setConditions] = useState(prefillConditions ?? {});

  // Form → Modal: sync internal state whenever the parent's prefill values change
  // (e.g. when the modal is reopened after the parent form has been edited, or
  // when a parent field updates while the modal is already open).
  useEffect(() => {
    setEthrisk(prefillEthrisk ?? patientEthrisk ?? 1);
  }, [prefillEthrisk, patientEthrisk]);

  useEffect(() => {
    setTotalCholesterol(normalizeDecimal(prefillTotalCholesterol));
  }, [prefillTotalCholesterol]);

  useEffect(() => {
    setHdl(normalizeDecimal(prefillHdl));
  }, [prefillHdl]);

  useEffect(() => {
    setSbp(normalizeDecimal(prefillSbp));
  }, [prefillSbp]);

  useEffect(() => {
    setSbps5(normalizeDecimal(prefillSbps5));
  }, [prefillSbps5]);

  useEffect(() => {
    setBmi(normalizeDecimal(prefillBmi));
  }, [prefillBmi]);

  useEffect(() => {
    setCigarettesPerDay(
      prefillCigarettesPerDay ? String(prefillCigarettesPerDay) : ""
    );
  }, [prefillCigarettesPerDay]);

  useEffect(() => {
    setConditions(prefillConditions ?? {});
  }, [prefillConditions]);

  // Modal → Form: notify parent on every field change so it can keep its own
  // state in sync without waiting for "Use score".
  const notifyFieldChange = (overrides) => {
    if (!onFieldChange) return;

    onFieldChange({
      bmi,
      sbp,
      totalCholesterol,
      hdl,
      sbps5,
      ethrisk,
      cigarettesPerDay,
      ...overrides,
    });
  };

  // QRisk3 uses the Total : HDL cholesterol ratio; derive it from the two
  // separate inputs so the clinician enters the raw lab values, not the ratio.
  const numTotalCholesterol = parseNumeric(totalCholesterol);
  const numHdl = parseNumeric(hdl);
  const hdlRatio =
    numTotalCholesterol && numHdl
      ? (numTotalCholesterol / numHdl).toFixed(2)
      : "";

  const [fieldErrors, setFieldErrors] = useState({});

  const smoke_cat = useMemo(
    () =>
      smokingStatusToSmokeCat(
        prefillSmokingStatus,
        Number(cigarettesPerDay) || 0
      ),
    [prefillSmokingStatus, cigarettesPerDay]
  );

  const isCurrentSmoker = prefillSmokingStatus === "CURRENT";

  const toggleCondition = (key) =>
    setConditions((prev) => ({ ...prev, [key]: prev[key] ? 0 : 1 }));

  const score = useMemo(() => {
    const numBmi = parseNumeric(bmi);
    const numSbp = parseNumeric(sbp);
    const numHdlRatio = parseNumeric(hdlRatio);
    if (!patientAge || !gender || !numBmi || !numSbp || !numHdlRatio)
      return null;

    return calculateQRisk3({
      age: patientAge,
      gender,
      bmi: numBmi,
      sbp: numSbp,
      sbps5: parseNumeric(sbps5) || 0,
      hdlRatio: numHdlRatio,
      smoke_cat,
      ethrisk,
      ...conditions,
    });
  }, [
    patientAge,
    gender,
    bmi,
    sbp,
    sbps5,
    hdlRatio,
    smoke_cat,
    ethrisk,
    conditions,
  ]);

  const risk = useMemo(
    () => (score != null ? getRiskLevel(score) : null),
    [score]
  );

  const handleClose = () => {
    setFieldErrors({});
    onClose();
  };

  const handleConfirm = () => {
    const errors = {};
    if (!gender) errors.gender = "Sex is required for QRisk3 calculation";
    if (!totalCholesterol)
      errors.totalCholesterol = "Total Cholesterol is required";
    if (!hdl) errors.hdl = "HDL Cholesterol is required";
    if (!sbp) errors.sbp = "Systolic BP is required";
    if (!bmi) errors.bmi = "BMI is required";

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    if (score == null) {
      const numBmi = parseNumeric(bmi);
      const numSbp = parseNumeric(sbp);
      const diagErrors = {};

      if (!patientAge) {
        diagErrors.patient =
          "Patient age not available — please ensure patient record is complete";
      } else if (!gender) {
        diagErrors.gender = "Sex is required for QRisk3 calculation";
      } else if (patientAge < 25 || patientAge > 84) {
        diagErrors.patient = `Patient age (${patientAge}) must be between 25 and 84 for QRisk3`;
      } else if (isNaN(numTotalCholesterol)) {
        diagErrors.totalCholesterol = `"${totalCholesterol}" is not a valid number`;
      } else if (isNaN(numHdl)) {
        diagErrors.hdl = `"${hdl}" is not a valid number`;
      } else if (isNaN(numSbp)) {
        diagErrors.sbp = `"${sbp}" is not a valid number`;
      } else if (isNaN(numBmi)) {
        diagErrors.bmi = `"${bmi}" is not a valid number`;
      } else {
        diagErrors.patient =
          "Could not calculate score — check all values are valid";
      }

      setFieldErrors(diagErrors);
      return;
    }

    setFieldErrors({});
    onConfirm(score.toFixed(1), hdlRatio, ethrisk, conditions, cigarettesPerDay, {
      totalCholesterol,
      hdl,
      sbps5,
    });
    handleClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      title="QRisk3 Calculator"
      width={880}
    >
      <div className={styles.body}>
        {/* Patient summary */}
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>Patient</h4>
          <div className={styles.patientInfo}>
            {!!patientAge && (
              <span className={styles.patientTag}>Age: {patientAge} yrs</span>
            )}
            {!!patientGender && (
              <span className={styles.patientTag}>
                {isMale ? "Male" : "Female"}
              </span>
            )}
            {!!prefillSmokingStatus && (
              <span className={styles.patientTag}>
                Smoking:{" "}
                {prefillSmokingStatus === "NEVER"
                  ? "Never"
                  : prefillSmokingStatus === "EX_SMOKER"
                  ? "Ex-smoker"
                  : "Current"}
              </span>
            )}
          </div>
          {!!fieldErrors.patient && (
            <p className={styles.fieldError}>{fieldErrors.patient}</p>
          )}
          {!patientGender && (
            <div className={styles.genderSelect}>
              <span className={styles.fieldLabel}>Sex</span>
              <Select
                options={[
                  { label: "Male", value: "MALE" },
                  { label: "Female", value: "FEMALE" },
                ]}
                value={gender}
                onChange={(value) => {
                  setGender(value);
                  if (fieldErrors.gender)
                    setFieldErrors((p) => ({ ...p, gender: undefined }));
                }}
                error={!!fieldErrors.gender}
              />
              {!!fieldErrors.gender && (
                <p className={styles.fieldError}>{fieldErrors.gender}</p>
              )}
            </div>
          )}
        </div>

        {/* Measurements */}
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>Measurements</h4>
          <div className={styles.grid2}>
            <div>
              <span className={styles.fieldLabel}>Total Cholesterol</span>
              <div className={styles.inputWithUnit}>
                <input
                  type="text"
                  inputMode="decimal"
                  className={`${styles.input} ${
                    fieldErrors.totalCholesterol ? styles.inputError : ""
                  }`}
                  placeholder="e.g. 5.0"
                  value={totalCholesterol}
                  onChange={(e) => {
                    const nextValue = normalizeDecimal(e.target.value);
                    setTotalCholesterol(nextValue);
                    notifyFieldChange({ totalCholesterol: nextValue });
                    if (fieldErrors.totalCholesterol)
                      setFieldErrors((p) => ({
                        ...p,
                        totalCholesterol: undefined,
                      }));
                  }}
                />
                <span className={styles.inputUnit}>mmol/L</span>
              </div>
              {fieldErrors.totalCholesterol && (
                <p className={styles.fieldError}>
                  {fieldErrors.totalCholesterol}
                </p>
              )}
            </div>
            <div>
              <span className={styles.fieldLabel}>HDL Cholesterol</span>
              <div className={styles.inputWithUnit}>
                <input
                  type="text"
                  inputMode="decimal"
                  className={`${styles.input} ${
                    fieldErrors.hdl ? styles.inputError : ""
                  }`}
                  placeholder="e.g. 1.2"
                  value={hdl}
                  onChange={(e) => {
                    const nextValue = normalizeDecimal(e.target.value);
                    setHdl(nextValue);
                    notifyFieldChange({ hdl: nextValue });
                    if (fieldErrors.hdl)
                      setFieldErrors((p) => ({ ...p, hdl: undefined }));
                  }}
                />
                <span className={styles.inputUnit}>mmol/L</span>
              </div>
              {fieldErrors.hdl ? (
                <p className={styles.fieldError}>{fieldErrors.hdl}</p>
              ) : (
                !!hdlRatio && (
                  <p className={styles.fieldHint}>
                    Total : HDL ratio = {hdlRatio}
                  </p>
                )
              )}
            </div>
            <div>
              <span className={styles.fieldLabel}>Systolic BP</span>
              <div className={styles.inputWithUnit}>
                <input
                  type="text"
                  inputMode="decimal"
                  className={`${styles.input} ${
                    fieldErrors.sbp ? styles.inputError : ""
                  }`}
                  placeholder="e.g. 130"
                  value={sbp}
                  onChange={(e) => {
                    const nextValue = normalizeDecimal(e.target.value);
                    setSbp(nextValue);
                    notifyFieldChange({ sbp: nextValue });
                    if (fieldErrors.sbp)
                      setFieldErrors((p) => ({ ...p, sbp: undefined }));
                  }}
                />
                <span className={styles.inputUnit}>mmHg</span>
              </div>
              {fieldErrors.sbp && (
                <p className={styles.fieldError}>{fieldErrors.sbp}</p>
              )}
            </div>
            <div>
              <span className={styles.fieldLabel}>
                Systolic BP standard deviation
              </span>
              <div className={styles.inputWithUnit}>
                <input
                  type="text"
                  inputMode="decimal"
                  className={styles.input}
                  placeholder="e.g. 12"
                  value={sbps5}
                  onChange={(e) => {
                    const nextValue = normalizeDecimal(e.target.value);
                    setSbps5(nextValue);
                    notifyFieldChange({ sbps5: nextValue });
                  }}
                />
                <span className={styles.inputUnit}>mmHg</span>
              </div>
              <p className={styles.fieldHint}>
                Optional — SD of the two most recent systolic BP readings
              </p>
            </div>
            <div>
              <span className={styles.fieldLabel}>BMI</span>
              <div className={styles.inputWithUnit}>
                <input
                  type="text"
                  inputMode="decimal"
                  className={`${styles.input} ${
                    fieldErrors.bmi ? styles.inputError : ""
                  }`}
                  placeholder="e.g. 27.5"
                  value={bmi}
                  onChange={(e) => {
                    const nextValue = normalizeDecimal(e.target.value);
                    setBmi(nextValue);
                    notifyFieldChange({ bmi: nextValue });
                    if (fieldErrors.bmi)
                      setFieldErrors((p) => ({ ...p, bmi: undefined }));
                  }}
                />
                <span className={styles.inputUnit}>kg/m²</span>
              </div>
              {fieldErrors.bmi && (
                <p className={styles.fieldError}>{fieldErrors.bmi}</p>
              )}
            </div>
            <div>
              <span className={styles.fieldLabel}>Ethnicity</span>
              <Select
                options={ETHNICITY_OPTIONS}
                value={ethrisk}
                onChange={(nextValue) => {
                  setEthrisk(nextValue);
                  notifyFieldChange({ ethrisk: nextValue });
                }}
              />
            </div>
            {isCurrentSmoker && (
              <div>
                <span className={styles.fieldLabel}>Cigarettes per day</span>
                <input
                  type="text"
                  inputMode="numeric"
                  className={styles.input}
                  placeholder="e.g. 10"
                  value={cigarettesPerDay}
                  onChange={(e) => {
                    const nextValue = e.target.value;
                    setCigarettesPerDay(nextValue);
                    notifyFieldChange({ cigarettesPerDay: nextValue });
                  }}
                />
                <p className={styles.fieldHint}>
                  &lt;10 = light · 10–19 = moderate · ≥20 = heavy
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Medical conditions */}
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>Medical history</h4>
          <div className={styles.conditionsGrid}>
            {CONDITIONS.filter(
              ({ key }) => isMale || key !== "b_impotence2"
            ).map(({ key, label, hint }) => (
              <label key={key} className={styles.checkboxRow}>
                <Checkbox
                  checked={!!conditions[key]}
                  onChange={() => toggleCondition(key)}
                />
                <span className={styles.checkboxLabel}>
                  {label}
                  {!!hint && <span className={styles.checkboxHint}>{hint}</span>}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Result */}
        {score != null && risk && (
          <div className={`${styles.result} ${RESULT_CLASS[risk.colorKey]}`}>
            <div>
              <div className={styles.resultLabel}>{risk.level}</div>
              <p className={styles.resultDescription}>{risk.description}</p>
            </div>
            <div
              className={`${styles.resultScore} ${SCORE_CLASS[risk.colorKey]}`}
            >
              {score.toFixed(1)}%
            </div>
          </div>
        )}

      </div>
      <DialogFooter>
        <Button variant="outline" onClick={handleClose}>
          Cancel
        </Button>
        <Button onClick={handleConfirm}>Use score</Button>
      </DialogFooter>
    </Dialog>
  );
};
